///
/// EarthBrowser - SQL Terminal Browser
///
/// Movable window for executing SQL queries against the local SQLite database
/// Created by Matt Giger on December 19, 2025
/// Copyright (c) 2025 Geodata Labs
///

import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { DatasetBrowserWindow } from '@/components/windows/DatasetBrowserWindow'
import { useRegisterWindowContent } from '@/components/windows/WindowContentRegistry'
import { SQLTerminal, type SQLTerminalHandle } from '@/components/features/dev-tools/SQLTerminal'
import type { DatasetBrowserWindowState } from '@/redux/dialogSlice'
import { Terminal } from 'lucide-react'

interface SQLTerminalBrowserRendererProps {
	windowId: string
	windowState: DatasetBrowserWindowState
}

export const SQLTerminalBrowserRenderer = memo(function SQLTerminalBrowserRenderer({
	windowId,
	windowState,
}: SQLTerminalBrowserRendererProps) {
	const terminalRef = useRef<SQLTerminalHandle>(null)

	// Focus the terminal when the window first opens or becomes visible
	useEffect(() => {
		if (windowState?.isVisible) {
			// Small delay to ensure terminal is mounted and ready
			const timer = setTimeout(() => {
				terminalRef.current?.focus()
			}, 100)
			return () => clearTimeout(timer)
		}
	}, [windowState?.isVisible])

	const handleBringToFront = () => {
		// Focus the terminal when window is brought to front
		terminalRef.current?.focus()
	}

	// Content for the window
	const content = useMemo(() => (
		<div className="h-full">
			<SQLTerminal ref={terminalRef} />
		</div>
	), [])

	// Register content with the window content registry for drawer docking
	const contentRenderer = useCallback(() => content, [content])
	useRegisterWindowContent(windowId, contentRenderer)

	return (
		<DatasetBrowserWindow
			windowState={windowState}
			icon={<Terminal className="w-5 h-5" />}
			iconColor="text-yellow-400"
			onBringToFront={handleBringToFront}
		>
			{content}
		</DatasetBrowserWindow>
	)
})

